import React, { useState, useCallback, useMemo, memo } from 'react';
import { ChevronDown, ChevronUp, ArrowLeft, Sparkles, Search, X, Radio, Tv } from 'lucide-react';
import MEDIA_FALLBACK from '../assets/images/ReactFirstR-mark.png';


// Keys here MUST match the backend `platform_counts` dict
// (reports/data_aggregator.py): twitter, tiktok, instagram, telegram, facebook_group, web.
// The backend folds rss + webz into a single "web" bucket.
const PLATFORM_LABELS = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook_group: 'Facebook',
  telegram: 'Telegram',
  twitter: 'X',
  tv: 'טלוויזיה',
  web: 'אחר',
};

// Keyed by the post `source` values and `platform_counts` keys the backend emits:
// twitter, tiktok, instagram, telegram, rss, webz, facebook_group, web.
const PLATFORM_COLORS = {
  twitter: '#1da1f2',
  tiktok: '#ff0050',
  instagram: '#e1306c',
  telegram: '#0088cc',
  facebook_group: '#1877f2',
  rss: '#22c55e',
  webz: '#10b981',
  web: '#10b981',
  tv: '#9f1239',
};

const PLATFORM_ICONS = {
  twitter: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000"/>
    </svg>
  ),
  tiktok: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.1V9.4a6.28 6.28 0 00-.82-.05A6.34 6.34 0 003.15 15.7 6.34 6.34 0 009.49 22a6.34 6.34 0 006.34-6.34V9.01a8.24 8.24 0 004.76 1.5V7.1a4.83 4.83 0 01-1-.41z" fill="#fe2c55"/>
    </svg>
  ),
  instagram: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig3)" strokeWidth="2"/>
      <circle cx="12" cy="12" r="5" stroke="url(#ig3)" strokeWidth="2"/>
      <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig3)"/>
      <defs><linearGradient id="ig3" x1="2" y1="22" x2="22" y2="2"><stop stopColor="#f09433"/><stop offset=".5" stopColor="#dc2743"/><stop offset="1" stopColor="#bc1888"/></linearGradient></defs>
    </svg>
  ),
  telegram: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#34aadf"/>
    </svg>
  ),
  rss: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="6.18" cy="17.82" r="2.18" fill="#ff6600"/>
      <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z" fill="#ff6600"/>
    </svg>
  ),
  webz: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#1a73e8" strokeWidth="2"/>
      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="#1a73e8" strokeWidth="2"/>
    </svg>
  ),
  facebook_group: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877f2"/>
    </svg>
  ),
  tv: (<Tv size={14} color="#9f1239" />),
};

// "web" is a backend platform_counts bucket (rss + webz folded together) — reuse the webz icon.
PLATFORM_ICONS.web = PLATFORM_ICONS.webz;

const formatNumber = (num) => {
  if (num == null) return null;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('he-IL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

// Bold *asterisk* spans, plus the org's own name and every competitor name — the same
// emphasis the PDF applies (pdf_builder._format_summary_html). Longest name first so
// "Osem-Nestlé" wins over "Osem". Case-insensitive, like the backend.
const buildBoldPattern = (names) => {
  const escaped = (names || [])
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegex);
  return new RegExp(`(\\*[^*]+\\*${escaped.length ? '|' + escaped.join('|') : ''})`, 'gi');
};

const renderBoldText = (text, boldPattern) => {
  if (!text) return text;
  const pattern = boldPattern || /(\*[^*]+\*)/g;
  const parts = String(text).split(pattern);
  return parts.map((part, i) => {
    if (!part) return null;
    if (i % 2 === 1) {
      const inner = part.startsWith('*') && part.endsWith('*') ? part.slice(1, -1) : part;
      return <strong key={i}>{inner}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// Map a post `source` to the platform-card bucket the backend uses
// (rss + webz + anything unknown fold into "web").
const bucketForSource = (src) => {
  const s = (src || '').toLowerCase();
  if (PLATFORM_LABELS[s]) return s; // twitter, tiktok, instagram, telegram, facebook_group, web
  return 'web';
};

// Mirror the backend sentiment thresholds (pdf_builder.py / daily_intelligence.html):
// null → neutral, < -0.2 → negative, > 0.2 → positive, else neutral.
const sentimentBucket = (score) => {
  if (score == null) return 'neu';
  if (score < -0.2) return 'neg';
  if (score > 0.2) return 'pos';
  return 'neu';
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// XSS-safe keyword highlighting: split on a combined case-insensitive regex and
// wrap matches in <mark>. Returns the original string when there's nothing to do.
const highlightContent = (text, keywords) => {
  if (!text || !keywords || keywords.length === 0) return text;
  const valid = keywords.filter(k => k && k.trim()).map(escapeRegex);
  if (valid.length === 0) return text;
  const re = new RegExp(`(${valid.join('|')})`, 'gi');
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <mark key={i} className="rp-mark">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const VerifiedBadge = () => (
  <svg className="rp-card__verified" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 1.5l2.6 1.9 3.2-.2 1 3.05 2.6 1.9-1 3.05 1 3.05-2.6 1.9-1 3.05-3.2-.2L12 22.5l-2.6-1.9-3.2.2-1-3.05-2.6-1.9 1-3.05-1-3.05 2.6-1.9 1-3.05 3.2.2z" fill="currentColor"/>
    <path d="M8.5 12.2l2.3 2.3 4.7-4.9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ReadOnlyPostCard = memo(({ post, highlightKeywords }) => {
  const source = post.source || 'twitter';
  const [insightsOpen, setInsightsOpen] = useState(false);
  const platformColor = PLATFORM_COLORS[source] || '#0288d1';

  const sentimentScore = post.sentiment_score != null ? Math.round(post.sentiment_score * 100) : null;
  // Classify on the RAW score, not the rounded x100 value, so the pill agrees with the
  // aggregate bar (sentimentBucket) and with the PDF (daily_intelligence.html sent_label).
  // Rounding first made 0.204 read "neutral" on the card while the bar counted it positive.
  const sentimentClass = post.sentiment_score != null
    ? (post.sentiment_score > 0.2 ? 'positive' : post.sentiment_score < -0.2 ? 'negative' : 'neutral')
    : null;

  const getSentimentLabel = () => {
    if (sentimentScore == null) return null;
    return sentimentScore > 0 ? `+${sentimentScore}` : `${sentimentScore}`;
  };

  return (
    <div
      className={`rp-card ${sentimentClass ? `rp-card--border-${sentimentClass}` : ''}`}
      style={{ '--pf': platformColor }}
    >
      <div className="rp-card__header">
        <div className="rp-card__author">
          {post.author_profile_image_url ? (
            <img
              src={post.author_profile_image_url}
              alt=""
              className="rp-card__avatar"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div
            className="rp-card__avatar-fallback"
            style={{ display: post.author_profile_image_url ? 'none' : 'flex' }}
          >
            {(post.author_name || post.author_username || '?')[0].toUpperCase()}
          </div>
          <div className="rp-card__author-info">
            <span className="rp-card__name">
              {post.author_name || post.author_username || 'Unknown'}
              {post.author_verified && <VerifiedBadge />}
            </span>
            {source === 'tv' ? (
              <span className="rp-card__handle">שידור טלוויזיה · TV</span>
            ) : post.author_username ? (
              <span className="rp-card__handle">@{post.author_username}</span>
            ) : null}
          </div>
          {post.duplicate_count > 1 && (
            // The aggregator collapses repeated posts (e.g. the same TV ad transcript
            // captured many times) into one card, but platform_counts still counts them
            // all. Without this badge the header total is larger than the number of cards
            // with nothing to explain the gap. The PDF shows the same badge.
            <span className="rp-card__dupes">×{post.duplicate_count} מופעים</span>
          )}
          {post.followers_count > 0 && (
            <span className="rp-card__followers">{formatNumber(post.followers_count)} עוקבים</span>
          )}
        </div>
        {PLATFORM_ICONS[source] && (
          <div className="rp-card__source-logo" aria-hidden="true">{PLATFORM_ICONS[source]}</div>
        )}
      </div>

      <div className="rp-card__body">
        {post.post_url ? (
          <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="rp-card__content-link">
            <div className="rp-card__content" dir="rtl">{highlightContent(post.content, highlightKeywords)}</div>
          </a>
        ) : (
          <div className="rp-card__content" dir="rtl">{highlightContent(post.content, highlightKeywords)}</div>
        )}
        {post.media_url && (
          <div className="rp-card__media">
            <img
              src={post.media_url}
              alt=""
              loading="lazy"
              onError={(e) => {
                // Source media expires (TikTok/IG CDN links rot). Show the ReactFirst
                // mark instead of the browser's broken-image glyph. Guard against a
                // loop if the fallback itself ever fails to load.
                if (e.target.dataset.fallback) return;
                e.target.dataset.fallback = '1';
                e.target.src = MEDIA_FALLBACK;
                e.target.closest('.rp-card__media')?.classList.add('rp-card__media--fallback');
              }}
            />
          </div>
        )}
      </div>

      <div className="rp-card__footer">
        <div className="rp-card__stats">
          {post.likes_count > 0 && (
            <span className="rp-card__stat">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
              {formatNumber(post.likes_count)}
            </span>
          )}
          {post.comments_count > 0 && (
            <span className="rp-card__stat">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              {formatNumber(post.comments_count)}
            </span>
          )}
          {post.shares_count > 0 && (
            <span className="rp-card__stat">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
              {formatNumber(post.shares_count)}
            </span>
          )}
          {post.views_count > 0 && (
            <span className="rp-card__stat">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              {formatNumber(post.views_count)}
            </span>
          )}
          {post.post_time && (
            <span className="rp-card__time">{post.post_time}</span>
          )}
        </div>
        <div className="rp-card__footer-right">
          {source === 'tv' && post.post_url && (
            <a
              href={post.post_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#9f1239', fontWeight: 700, fontSize: 12, textDecoration: 'none' }}
            >
              ▶ צפה בקליפ
            </a>
          )}
          {sentimentScore != null && (
            <span className={`rp-card__sentiment rp-card__sentiment--${sentimentClass}`}>
              {getSentimentLabel()}
            </span>
          )}
          {post.post_url && (
            <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="rp-card__external">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
            </a>
          )}
        </div>
      </div>

      {(post.summary || post.reason) && (
        <div className="rp-card__insights">
          <button
            className={`rp-card__insights-btn ${insightsOpen ? 'rp-card__insights-btn--open' : ''}`}
            onClick={() => setInsightsOpen(!insightsOpen)}
          >
            <Sparkles size={12} className="rp-card__insights-spark" />
            <span>{insightsOpen ? 'הסתר תובנות' : 'תובנות AI'}</span>
            {insightsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {insightsOpen && (
            <div className="rp-card__insights-body">
              {post.summary && (
                <div className="rp-card__insight-row">
                  <span className="rp-card__insight-label">סיכום</span>
                  <span className="rp-card__insight-text" dir="rtl">{post.summary}</span>
                </div>
              )}
              {post.reason && (
                <div className="rp-card__insight-row">
                  <span className="rp-card__insight-label">סיבה</span>
                  <span className="rp-card__insight-text" dir="rtl">{post.reason}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {source === 'twitter' && Array.isArray(post.dependencies) && post.dependencies.length > 0 && (
        <div className="rp-card__deps">
          {post.dependencies.map((dep, i) => {
            if (typeof dep !== 'object' || dep === null) return null;
            const depName = dep.name || dep.username || '';
            const isRtl = dep.content ? /[֐-׿؀-ۿ]/.test(dep.content) : true;
            return (
              <div key={i} className="rp-dep">
                {dep.relation_label && (
                  <div className="rp-dep__rel">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 17l5-5-5-5"/><path d="M20 12H9a4 4 0 00-4 4v2"/></svg>
                    {dep.relation_label}{dep.username ? ` @${dep.username}` : ''}
                  </div>
                )}
                {dep.username && (
                  <div className="rp-dep__head">
                    {dep.profile_image_url ? (
                      <img className="rp-dep__avatar-img" src={dep.profile_image_url} alt=""
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="rp-dep__avatar">{(depName || '?')[0].toUpperCase()}</span>
                    )}
                    <span className="rp-dep__name">{depName}</span>
                    <span className="rp-dep__handle">@{dep.username}</span>
                  </div>
                )}
                {dep.content && (
                  <div className="rp-dep__body" dir={isRtl ? 'rtl' : 'ltr'}>
                    {dep.content.slice(0, 200)}{dep.content.length > 200 ? '…' : ''}
                  </div>
                )}
                {dep.url && (
                  <a className="rp-dep__open" href={dep.url} target="_blank" rel="noopener noreferrer">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    פתח את הציוץ המקורי
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {post.comment_count > 0 && (
        <a
          className="rp-card__comments-bar"
          href={post.comments_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            margin: '8px 10px 10px', padding: '7px 11px', borderRadius: 10,
            background: '#f1f5f9', border: '1px solid #e2e8f0', textDecoration: 'none',
            color: '#334155', fontSize: 12, fontWeight: 600,
          }}
          dir="rtl"
        >
          <span>💬 {post.comment_count} תגובות</span>
          <span style={{ display: 'flex', gap: 6 }}>
            {post.comments_avg_content != null && (() => {
              const c = Math.round(post.comments_avg_content * 100);
              const cls = post.comments_avg_content > 0.2 ? 'positive' : post.comments_avg_content < -0.2 ? 'negative' : 'neutral';
              return <span className={`rp-card__sentiment rp-card__sentiment--${cls}`}>תוכן {c > 0 ? '+' : ''}{c}</span>;
            })()}
            {(() => {
              const b = post.comments_avg_brand || 0;
              const bv = Math.round(b * 100);
              const cls = b > 0.2 ? 'positive' : b < -0.2 ? 'negative' : 'neutral';
              return <span className={`rp-card__sentiment rp-card__sentiment--${cls}`}>אנחנו {bv > 0 ? '+' : ''}{bv}</span>;
            })()}
          </span>
          <span style={{ marginInlineStart: 'auto', color: '#1a73e8' }}>צפה בתגובות ←</span>
        </a>
      )}
    </div>
  );
});

const SentimentDistBar = ({ dist, title }) => {
  if (!dist || dist.total <= 0) return null;
  const { neg_pct, neu_pct, pos_pct, pos, neu, neg } = dist;
  return (
    <div className="rp-dist">
      {title && <div className="rp-dist__title">{title}</div>}
      <div className="rp-sentiment-chart__dist-bar">
        {neg_pct > 0 && <div className="rp-sentiment-chart__dist-seg" style={{ width: `${neg_pct}%`, backgroundColor: '#ef4444' }}>{neg_pct}%</div>}
        {neu_pct > 0 && <div className="rp-sentiment-chart__dist-seg" style={{ width: `${neu_pct}%`, backgroundColor: '#f59e0b' }}>{neu_pct}%</div>}
        {pos_pct > 0 && <div className="rp-sentiment-chart__dist-seg" style={{ width: `${pos_pct}%`, backgroundColor: '#10b981' }}>{pos_pct}%</div>}
      </div>
      <div className="rp-sentiment-chart__legend">
        <span><span className="rp-dot rp-dot--positive" />חיובי ({pos})</span>
        <span><span className="rp-dot rp-dot--neutral" />ניטרלי ({neu})</span>
        <span><span className="rp-dot rp-dot--negative" />שלילי ({neg})</span>
      </div>
    </div>
  );
};

const Sentiment30DChart = ({ sentiment30d, sentiment30dTotals }) => {
  if (!sentiment30d || sentiment30d.length === 0) return null;

  const totals = sentiment30dTotals || {};
  const total = totals.total || 0;
  const posPct = total > 0 ? Math.round((totals.positive || 0) / total * 100) : 0;
  const neuPct = total > 0 ? Math.round((totals.neutral || 0) / total * 100) : 0;
  const negPct = total > 0 ? Math.round((totals.negative || 0) / total * 100) : 0;

  return (
    <div className="rp-section">
      <h2 className="rp-section__title">סנטימנט מצטבר — 30 ימים</h2>
      <div className="rp-sentiment-chart">
        <div className="rp-sentiment-chart__bars">
          {sentiment30d.map((day, i) => {
            const dayTotal = day.positive + day.neutral + day.negative;
            return (
              <div key={i} className="rp-sentiment-chart__bar-col">
                {dayTotal > 0 ? (
                  <>
                    {day.negative > 0 && <div style={{ height: `${(day.negative / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#ef4444' }} />}
                    {day.neutral > 0 && <div style={{ height: `${(day.neutral / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#f59e0b' }} />}
                    {day.positive > 0 && <div style={{ height: `${(day.positive / dayTotal * 100).toFixed(1)}%`, backgroundColor: '#10b981' }} />}
                  </>
                ) : (
                  <div style={{ height: '100%', backgroundColor: '#e2e8f0' }} />
                )}
              </div>
            );
          })}
        </div>
        <div className="rp-sentiment-chart__labels">
          {sentiment30d.map((day, i) => (
            <div key={i} className="rp-sentiment-chart__label">
              {i % 2 === 0 ? day.day : ''}
            </div>
          ))}
        </div>
        {total > 0 && (
          <>
            <div className="rp-sentiment-chart__dist-bar">
              {negPct > 0 && <div style={{ width: `${negPct}%`, backgroundColor: '#ef4444' }} className="rp-sentiment-chart__dist-seg">{negPct}%</div>}
              {neuPct > 0 && <div style={{ width: `${neuPct}%`, backgroundColor: '#f59e0b' }} className="rp-sentiment-chart__dist-seg">{neuPct}%</div>}
              {posPct > 0 && <div style={{ width: `${posPct}%`, backgroundColor: '#10b981' }} className="rp-sentiment-chart__dist-seg">{posPct}%</div>}
            </div>
            <div className="rp-sentiment-chart__legend">
              <span><span className="rp-dot rp-dot--positive" />חיובי {posPct}% ({totals.positive || 0})</span>
              <span><span className="rp-dot rp-dot--neutral" />ניטרלי {neuPct}% ({totals.neutral || 0})</span>
              <span><span className="rp-dot rp-dot--negative" />שלילי {negPct}% ({totals.negative || 0})</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Self-contained, login-free report renderer. Given a loaded `reportData`
// ({ org_name, report_date, report_data }) it renders the full report body:
// beige header + centered platform counters + sentiment + 30d chart + summary +
// connect-the-dots + per-list posts. Used by the dashboard (DailyReportsScreen)
// and the standalone public page (PublicReportPage).
//
// Props:
//   reportData  — { org_name, report_date, report_data }
//   isMobile    — boolean (layout tweaks)
//   onBack      — optional () => void; when provided + isMobile, shows a back button
//   liveControls — optional { liveMode, onToggleLive, loading }; when provided AND
//                  report_data.live_available, shows the live-toggle button (dashboard only)
const DailyReportView = ({ reportData, isMobile, onBack, liveControls }) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Counters + daily sentiment derive from the FULL report's hourly posts (not the
  // in-report search filter), so they always equal the full displayed post set.
  const derived = useMemo(() => {
    const hourly = reportData?.report_data?.hourly_posts_by_list || [];
    const counts = { twitter: 0, tiktok: 0, instagram: 0, telegram: 0, facebook_group: 0, web: 0 };
    let neg = 0, neu = 0, pos = 0;
    for (const section of hourly) {
      for (const hourGroup of (section.hours || [])) {
        for (const post of (hourGroup.posts || [])) {
          counts[bucketForSource(post.source)]++;
          const b = sentimentBucket(post.sentiment_score);
          if (b === 'neg') neg++; else if (b === 'pos') pos++; else neu++;
        }
      }
    }
    const total = neg + neu + pos;
    const dist = {
      neg, neu, pos, total,
      neg_pct: total ? Math.round(neg / total * 100) : 0,
      neu_pct: total ? Math.round(neu / total * 100) : 0,
      pos_pct: total ? Math.round(pos / total * 100) : 0,
    };
    return { counts, total, dist };
  }, [reportData]);

  const toggleSection = useCallback((listId) => {
    setCollapsedSections(prev => ({ ...prev, [listId]: !prev[listId] }));
  }, []);

  if (!reportData) return null;

  const rd = reportData.report_data || {};
  const trends = rd.trends;
  const liveAvailable = !!rd.live_available;
  const isLive = !!rd.is_live;
  const hourlyPostsByList = rd.hourly_posts_by_list || [];
  const summaryBullets = Array.isArray(rd.summary_text) ? rd.summary_text : (rd.summary_text ? [rd.summary_text] : []);
  const sentiment30d = rd.sentiment_30d || [];
  const sentiment30dTotals = rd.sentiment_30d_totals || {};
  const summaryBoldPattern = buildBoldPattern([rd.company_name, ...(rd.competitor_names || [])]);

  const searchLower = searchQuery.toLowerCase();
  const matchesSearch = (post) => !searchQuery || post.content?.toLowerCase().includes(searchLower);
  const filteredHourlyPosts = hourlyPostsByList.map(section => ({
    ...section,
    hours: (section.hours || []).map(h => ({ ...h, posts: (h.posts || []).filter(matchesSearch) })).filter(h => h.posts.length > 0)
  })).filter(s => s.hours.length > 0);

  return (
    <div className="rp-detail">
      {isMobile && onBack && (
        <button className="rp-detail__back" onClick={onBack}>
          <ArrowLeft size={16} />
          <span>כל הדו״חות</span>
        </button>
      )}
      <div className="rp-report-head">
        {liveAvailable && liveControls && (
          <button
            className={`rp-live-btn rp-report-head__live ${liveControls.liveMode ? 'rp-live-btn--on' : ''}`}
            onClick={liveControls.onToggleLive}
            disabled={liveControls.loading}
          >
            <Radio size={15} />
            <span>{liveControls.liveMode ? 'תצוגת 08:00–08:00' : 'הצג נתונים חיים'}</span>
          </button>
        )}
        <div className="rp-report-head__title">
          <h1 className="rp-report-head__org">{reportData.org_name}</h1>
          <div className="rp-report-head__date">
            {formatDate(reportData.report_date)}
            {isLive && <span className="rp-detail__live-tag"><span className="rp-detail__live-dot" />חי · עד עכשיו</span>}
          </div>
        </div>

        <div className="rp-platform-row">
          {Object.entries(PLATFORM_LABELS).map(([key, label]) => (
            <div key={key} className="rp-platform-card">
              <div className="rp-platform-card__icon">{PLATFORM_ICONS[key]}</div>
              <div className="rp-platform-card__count">{rd.platform_counts?.[key] ?? derived.counts[key] ?? 0}</div>
              <div className="rp-platform-card__label">{label}</div>
            </div>
          ))}
        </div>

        <div className="rp-totals">
          <span className="rp-totals__count">{((rd.platform_counts?.total ?? derived.total) || 0).toLocaleString()}</span>
          <span className="rp-totals__label">אזכורים</span>
          {trends && (
            <span className="rp-totals__trends">
              <span className={`rp-totals__pct ${trends.pct_change > 0 ? 'rp-totals__pct--up' : trends.pct_change < 0 ? 'rp-totals__pct--down' : ''}`}>
                שינוי מהשבוע: {trends.pct_change > 0 ? '+' : ''}{trends.pct_change}%
              </span>
              <span className="rp-totals__avg">ממוצע שבועי: {trends.avg_7d_total}</span>
            </span>
          )}
        </div>
      </div>

      <div className="rp-detail__content">
        <div className="rp-search">
          <Search size={16} className="rp-search__icon" />
          <input
            className="rp-search__input"
            type="text"
            placeholder="חיפוש בדו״ח..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            dir="rtl"
          />
          {searchQuery && (
            <button className="rp-search__clear" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>

        {derived.dist.total > 0 && (
          <div className="rp-section">
            <h2 className="rp-section__title">
              פילוח סנטימנט הפוסטים היומי
              {trends?.today_sentiment_avg != null && (
                <span className="rp-section__sub">
                  {' '}(סנטימנט מותג: {trends.today_sentiment_avg > 0 ? '+' : ''}{Math.round(trends.today_sentiment_avg * 100)}{trends.prev_sentiment_avg != null ? `, שבועי: ${Math.round(trends.prev_sentiment_avg * 100)}` : ''})
                </span>
              )}
            </h2>
            <div className="rp-sentiment-chart">
              <SentimentDistBar dist={derived.dist} />
            </div>
          </div>
        )}

        <Sentiment30DChart sentiment30d={sentiment30d} sentiment30dTotals={sentiment30dTotals} />

        {summaryBullets.length > 0 && summaryBullets[0] && (
          <div className="rp-section">
            <h2 className="rp-section__title">סיכום יומי</h2>
            <div className="rp-summary-box">
              {summaryBullets.filter(b => b.trim()).map((bullet, i) => (
                <div key={i} className="rp-summary-bullet">
                  <div className="rp-summary-bullet__dot" />
                  <div className="rp-summary-bullet__text" dir="rtl">{renderBoldText(bullet, summaryBoldPattern)}</div>
                </div>
              ))}
            </div>
          </div>
        )}


        {rd.connect_the_dots && (rd.connect_the_dots.headline || rd.connect_the_dots.today_events?.length > 0 || rd.connect_the_dots.connections?.length > 0) && (
          <div className="rp-section">
            <h2 className="rp-section__title">חיבור הנקודות</h2>
            {rd.connect_the_dots.headline && (
              <div dir="rtl" style={{ background: 'linear-gradient(270deg,#e7f0fb,#f0e8df)', borderRight: '4px solid #1d5a9e', borderRadius: 12, padding: '12px 16px', marginBottom: 12, fontWeight: 700, color: '#17365d', lineHeight: 1.55 }}>
                {rd.connect_the_dots.headline}
              </div>
            )}
            {(rd.connect_the_dots.today_events || []).map((e, i) => (
              <div key={i} dir="rtl" style={{ background: '#fff', border: '1px solid #d8cfc4', borderRadius: 12, padding: '11px 15px', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: '#17365d', marginBottom: 4 }}>{e.title}</div>
                {e.what && <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 3 }}><span style={{ color: '#1d5a9e', fontWeight: 700 }}>מה קרה:</span> {e.what}</div>}
                {e.why && <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 3 }}><span style={{ color: '#1d5a9e', fontWeight: 700 }}>למה:</span> {e.why}</div>}
                {e.past_link && <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 3 }}><span style={{ color: '#1d5a9e', fontWeight: 700 }}>הקשר לעבר:</span> {e.past_link}</div>}
                {e.brand_implication && <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 3 }}><span style={{ color: '#1d5a9e', fontWeight: 700 }}>משמעות:</span> {e.brand_implication}</div>}
              </div>
            ))}
            {(rd.connect_the_dots.connections || []).length > 0 && (
              <>
                <h2 className="rp-section__title" style={{ marginTop: 12 }}>נקודות מרכזיות</h2>
                {rd.connect_the_dots.connections.map((c, i) => (
                  <div key={i} dir="rtl" style={{ background: '#f0e8df', borderRadius: 12, padding: '11px 15px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, color: '#17365d', marginBottom: 3 }}>{c.title}</div>
                    <div style={{ fontSize: 13, lineHeight: 1.55, color: '#2d334a' }}>{c.story}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {filteredHourlyPosts.map(section => {
          const isCollapsed = collapsedSections[section.list_id];
          const totalSectionPosts = (section.hours || []).reduce((sum, h) => sum + (h.posts || []).length, 0);

          return (
            <div key={section.list_id} className="rp-list-section">
              <div className="rp-list-section__header" onClick={() => toggleSection(section.list_id)}>
                <div className="rp-list-section__left">
                  <span className="rp-list-section__name">{section.list_name}</span>
                  <span className="rp-list-section__badge">{totalSectionPosts}</span>
                </div>
                {isCollapsed ? <ChevronDown size={20} color="#64748b" /> : <ChevronUp size={20} color="#64748b" />}
              </div>

              {!isCollapsed && (
                <div className="rp-list-section__body">
                  {section.brand_sentiment_dist?.total > 0 && (
                    <div className="rp-list-section__dist">
                      <div className="rp-sentiment-chart">
                        <SentimentDistBar dist={section.brand_sentiment_dist} title={`פילוח סנטימנט מותג — ${section.list_name}`} />
                      </div>
                    </div>
                  )}
                  {(section.hours || []).map(hourGroup => (
                    <div key={hourGroup.label} className="rp-hour-group">
                      <div className="rp-hour-group__label">{hourGroup.label}</div>
                      <div className="rp-posts-grid">
                        {(hourGroup.posts || []).map((post, i) => (
                          <ReadOnlyPostCard key={`${section.list_id}-${hourGroup.label}-${i}-${post.platform_id}-${post.source}`} post={post} highlightKeywords={section.highlight_keywords} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {searchQuery && filteredHourlyPosts.length === 0 && (
          <div className="rp-search__no-results">
            <Search size={32} color="#94a3b8" />
            <span>לא נמצאו תוצאות עבור "{searchQuery}"</span>
          </div>
        )}

        {/* A report with no posts must say so. The PDF prints this line; without it the
            web page just ends after the summary and reads like a loading failure. */}
        {!searchQuery && hourlyPostsByList.length === 0 && (
          <div className="rp-search__no-results">
            <span>אין פוסטים רלוונטיים מ-24 השעות האחרונות.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const REPORT_STYLES = `
.rp-page {
  --accent: #0288d1;
  --card-bg: #ffffff;
  --page-bg: #fafafa;
  --surface: #f9fafb;
  --border: #e5e7eb;
  --border-strong: #d1d5db;
  --text-primary: #111827;
  --text-secondary: #374151;
  --text-muted: #9ca3af;
  --positive: #10b981;
  --negative: #ef4444;
  --neutral: #f59e0b;
  --radius: 12px;
  --radius-sm: 8px;
  --shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: var(--page-bg);
  direction: rtl;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
}

/* ── Divider ───────────────────────────────────────────────────────── */

.rp-divider {
  width: 5px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  position: relative;
  z-index: 10;
  transition: background 0.15s;
}

.rp-divider:hover, .rp-divider:active {
  background: #f3f4f6;
}

.rp-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1px;
  height: 32px;
  background: var(--border-strong);
  border-radius: 1px;
}

/* ── Sidebar ───────────────────────────────────────────────────────── */

.rp-sidebar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  background: var(--page-bg);
  border-left: 1px solid var(--border);
  flex-shrink: 0;
}

.rp-sidebar__header {
  background: #ffffff;
  padding: 20px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.rp-sidebar__title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 2px 0;
}

.rp-sidebar__subtitle {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

.rp-sidebar__list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px;
}

.rp-sidebar__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.rp-sidebar__error {
  color: var(--negative);
  font-weight: 600;
  text-align: center;
  padding: 24px;
}

.rp-sidebar__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 16px;
  color: var(--text-muted);
  font-size: 14px;
}

/* ── Report Item (sidebar) ─────────────────────────────────────────── */

.rp-report-item {
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  margin-bottom: 4px;
  border: 1px solid transparent;
  transition: background 0.12s, border-color 0.12s;
}

.rp-report-item:hover {
  background: var(--card-bg);
  border-color: var(--border);
}

.rp-report-item--selected {
  background: #f0f9ff;
  border-color: #7dd3fc;
  box-shadow: var(--shadow);
}

.rp-report-item__date {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ── Detail Panel ──────────────────────────────────────────────────── */

.rp-detail {
  flex: 1;
  overflow-y: auto;
  height: 100%;
}

.rp-detail--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.rp-detail__empty-text {
  font-size: 16px;
  color: var(--text-muted);
  font-weight: 600;
}

.rp-detail__back {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  margin: 16px 20px 0;
  transition: background 0.15s;
}

.rp-detail__back:hover {
  background: #f3f4f6;
}

.rp-detail__header {
  background: #ffffff;
  padding: 20px 24px;
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.rp-detail__header-main {
  min-width: 0;
}

.rp-live-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary);
  background: var(--surface);
  border: 1px solid var(--border-strong);
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.rp-live-btn:hover:not(:disabled) {
  border-color: var(--negative);
  color: var(--negative);
}

.rp-live-btn:disabled { opacity: 0.55; cursor: default; }

.rp-live-btn--on {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08);
}

.rp-detail__live-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-inline-start: 10px;
  color: #dc2626;
  font-weight: 700;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 2px 9px;
  border-radius: 8px;
  font-size: 11.5px;
}

.rp-detail__live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ef4444;
  animation: rp-live-pulse 1.4s ease-in-out infinite;
}

@keyframes rp-live-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
  50% { opacity: 0.6; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0); }
}

.rp-detail__title {
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
}

.rp-detail__date {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
  font-weight: 600;
}

.rp-detail__content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 20px 60px;
}

/* ── Loading ────────────────────────────────────────────────────────── */

.rp-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: rp-spin 0.8s linear infinite;
}

@keyframes rp-spin {
  to { transform: rotate(360deg); }
}

/* ── Platform Stats (pill chips) ────────────────────────────────────── */

/* Branded beige header — mirrors the PDF "Daily Report" banner */
.rp-report-head {
  position: relative;
  background: #f9f2f0;
  padding: 18px 0;
  border-bottom: 1px solid rgba(23, 54, 93, 0.07);
}
.rp-report-head__live {
  position: absolute;
  top: 16px;
  inset-inline-start: 16px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.92);
}
.rp-report-head__title {
  text-align: center;
  padding: 4px 20px 6px;
}
.rp-report-head__org {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #17365d;
  letter-spacing: -0.02em;
}
.rp-report-head__date {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 600;
}

.rp-platform-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 1000px;
  margin: 12px auto 0;
  padding: 0 16px;
}

.rp-platform-card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(23, 54, 93, 0.08);
  border-radius: 14px;
  padding: 12px 8px 10px;
  width: 104px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  transition: box-shadow 0.15s, transform 0.15s;
}
.rp-platform-card:hover {
  box-shadow: 0 6px 16px -8px rgba(23, 54, 93, 0.25);
  transform: translateY(-1px);
}
.rp-platform-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
}
.rp-platform-card__icon svg { width: 26px; height: 26px; }
.rp-platform-card__count {
  font-size: 22px;
  font-weight: 800;
  color: #17365d;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.rp-platform-card__label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 600;
}

/* ── Sections ───────────────────────────────────────────────────────── */

.rp-section {
  margin-bottom: 32px;
}

.rp-section__title {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
}

.rp-section__title::before {
  content: '';
  width: 4px;
  height: 17px;
  border-radius: 2px;
  background: var(--accent);
  flex-shrink: 0;
}

/* ── Sentiment Chart ────────────────────────────────────────────────── */

.rp-sentiment-chart {
  background-color: var(--card-bg);
  border-radius: var(--radius);
  padding: 24px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.rp-sentiment-chart__bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 90px;
  direction: ltr;
  margin-bottom: 6px;
}

.rp-sentiment-chart__bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
}

.rp-sentiment-chart__labels {
  display: flex;
  gap: 3px;
  direction: ltr;
  margin-bottom: 14px;
}

.rp-sentiment-chart__label {
  flex: 1;
  text-align: center;
  font-size: 9px;
  color: var(--text-muted);
  overflow: hidden;
}

.rp-sentiment-chart__dist-bar {
  display: flex;
  height: 26px;
  border-radius: 13px;
  overflow: hidden;
  direction: ltr;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.rp-sentiment-chart__dist-seg {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
}

.rp-sentiment-chart__legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-secondary);
  direction: rtl;
  font-weight: 600;
}

.rp-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 5px;
  vertical-align: middle;
}

.rp-dot--positive { background-color: #10b981; }
.rp-dot--neutral { background-color: #f59e0b; }
.rp-dot--negative { background-color: #ef4444; }

/* ── Summary Box ────────────────────────────────────────────────────── */

.rp-summary-box {
  background: linear-gradient(180deg, #fff, var(--surface));
  border-radius: var(--radius);
  padding: 22px 24px;
  border: 1px solid var(--border);
  border-right: 3px solid var(--accent);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rp-summary-bullet {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.rp-summary-bullet__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--accent);
  margin-top: 8px;
  flex-shrink: 0;
}

.rp-summary-bullet__text {
  font-size: 14px;
  line-height: 24px;
  color: var(--text-primary);
}

/* ── Posts Grid ─────────────────────────────────────────────────────── */

.rp-posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
}

@media (max-width: 768px) {
  .rp-posts-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  /* ── Phone layout ────────────────────────────────────────────────────
     The report is read on a phone far more often than on a desktop (it
     arrives as an email link). Everything below is sized for a 390px
     screen. */

  .rp-detail__content { padding: 0 12px 32px; }

  /* Platform tiles: a fixed 104px card wrapped to 2 columns, leaving a
     stranded 7th tile centred on its own row. A 4-up grid of compact
     tiles fills the width and reads as one block. */
  .rp-platform-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    padding: 0 12px;
    max-width: none;
  }
  .rp-platform-card {
    width: auto;
    padding: 9px 4px 8px;
    gap: 4px;
    border-radius: 12px;
  }
  .rp-platform-card__icon { height: 22px; }
  .rp-platform-card__icon svg { width: 20px; height: 20px; }
  .rp-platform-card__count { font-size: 17px; }
  .rp-platform-card__label { font-size: 10px; }

  /* Headline figure: give the count its own line instead of squeezing
     count + change + weekly-average onto one cramped row. */
  .rp-totals {
    gap: 4px 10px;
    padding: 0 12px;
    margin-top: 12px;
  }
  .rp-totals__count { font-size: 26px; }
  .rp-totals__trends { gap: 8px; }
  .rp-totals__pct, .rp-totals__avg { font-size: 12px; }

  .rp-report-head { padding-top: 8px; }
  .rp-report-head__title { font-size: 20px; }

  /* The title and its parenthetical sub-line collided and wrapped into each
     other. Drop the sub onto its own row (flex-basis 100%), indented past the
     4px accent bar + 9px gap so it aligns under the title text. Stacking the
     whole title with flex-direction:column would move that accent bar on top
     of the text, so we wrap instead. */
  .rp-section__title {
    font-size: 15px;
    flex-wrap: wrap;
    row-gap: 3px;
  }
  .rp-section__sub {
    flex-basis: 100%;
    margin-inline-start: 13px;
    font-size: 10px;
  }

  /* 30-day chart: 15 date labels do not fit in 390px — they overflowed
     their columns and rendered as clipped digits. Show every 6th day. */
  .rp-sentiment-chart { padding: 12px 8px; }
  .rp-sentiment-chart__label {
    font-size: 9px;
    /* A single day's column is ~10px wide, far narrower than "13/06". The
       neighbours are hidden below, so let the kept label spill over them
       rather than clipping it to "13". */
    overflow: visible;
    white-space: nowrap;
  }
  .rp-sentiment-chart__label:not(:nth-child(6n + 1)) {
    visibility: hidden;
  }
  .rp-sentiment-chart__legend {
    gap: 10px;
    font-size: 11px;
    flex-wrap: wrap;
  }
  .rp-sentiment-chart__dist-seg { font-size: 10px; }

  /* Back-to-list control: make it a comfortable tap target. */
  .rp-detail__back {
    padding: 10px 14px;
    font-size: 13px;
  }
}

@media (min-width: 1400px) {
  .rp-posts-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* ── Unified Post Card ──────────────────────────────────────────────── */

@keyframes rp-card-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.rp-posts-grid > .rp-card {
  animation: rp-card-in 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
}

.rp-posts-grid > .rp-card:nth-child(1) { animation-delay: 0s; }
.rp-posts-grid > .rp-card:nth-child(2) { animation-delay: 0.03s; }
.rp-posts-grid > .rp-card:nth-child(3) { animation-delay: 0.06s; }
.rp-posts-grid > .rp-card:nth-child(4) { animation-delay: 0.09s; }
.rp-posts-grid > .rp-card:nth-child(5) { animation-delay: 0.12s; }
.rp-posts-grid > .rp-card:nth-child(6) { animation-delay: 0.15s; }
.rp-posts-grid > .rp-card:nth-child(7) { animation-delay: 0.18s; }
.rp-posts-grid > .rp-card:nth-child(8) { animation-delay: 0.21s; }
.rp-posts-grid > .rp-card:nth-child(9) { animation-delay: 0.24s; }

.rp-card {
  --pf: #0288d1;
  background-color: var(--card-bg);
  border-radius: var(--radius);
  padding: 18px;
  position: relative;
  border: 1px solid var(--border);
  border-left: 3px solid transparent;
  box-shadow: var(--shadow);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.rp-card:hover {
  box-shadow: 0 10px 28px -8px rgba(0, 0, 0, 0.18);
  transform: translateY(-2px);
  border-color: var(--border-strong);
}

/* Platform-tinted folded corner (top-start) */
.rp-card__source-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  opacity: 0.95;
}
.rp-card__source-logo svg { width: 18px; height: 18px; }

.rp-card--border-positive { border-left-color: var(--positive); }
.rp-card--border-negative { border-left-color: var(--negative); }
.rp-card--border-neutral  { border-left-color: var(--neutral); }

/* Card Header */
.rp-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.rp-card__author {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.rp-card__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--border);
}

.rp-card__avatar-fallback {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, var(--pf), color-mix(in srgb, var(--pf) 60%, #000));
  flex-shrink: 0;
}

.rp-card__author-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.rp-card__name {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
}

.rp-card__verified {
  color: var(--pf);
  flex-shrink: 0;
}

.rp-card__handle {
  font-size: 11px;
  color: var(--text-muted);
  direction: ltr;
  text-align: right;
  font-weight: 500;
}

/* Card Body */
.rp-card__body {
  flex: 1;
  margin-bottom: 14px;
}

.rp-card__content-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.rp-card__content-link:hover .rp-card__content {
  color: #2563eb;
}

.rp-card__content {
  font-size: 14.5px;
  line-height: 23px;
  color: var(--text-primary);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  transition: color 0.15s;
}

.rp-card__media {
  margin-top: 12px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 1px solid var(--border);
  position: relative;
}

.rp-card__media img {
  width: 100%;
  max-height: 190px;
  object-fit: cover;
  display: block;
}

/* Media that failed to load (expired TikTok/IG CDN link): show the ReactFirst mark,
   centred and muted, instead of the browser's broken-image glyph. */
.rp-card__media--fallback {
  background: var(--surface-2, #f7f7f8);
}

.rp-card__media--fallback img {
  width: auto;
  height: 48px;
  max-height: 48px;
  object-fit: contain;
  margin: 32px auto;
  opacity: 0.28;
}

/* Card Footer */
.rp-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  gap: 8px;
}

.rp-card__stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rp-card__stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 600;
}

.rp-card__time {
  font-size: 11px;
  color: var(--text-muted);
}

.rp-card__footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Sentiment Pill */
.rp-card__sentiment {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 9px;
  font-size: 11.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.rp-card__sentiment--positive { background-color: #ecfdf5; color: #059669; box-shadow: inset 0 0 0 1px #a7f3d0; }
.rp-card__sentiment--negative { background-color: #fef2f2; color: #dc2626; box-shadow: inset 0 0 0 1px #fecaca; }
.rp-card__sentiment--neutral  { background-color: #fffbeb; color: #d97706; box-shadow: inset 0 0 0 1px #fde68a; }

/* External Link */
.rp-card__external {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: var(--text-muted);
  transition: color 0.15s, background-color 0.15s;
}

.rp-card__external:hover {
  color: var(--accent);
  background-color: var(--surface);
}

/* AI Insights (collapsible) */
.rp-card__insights {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}

.rp-card__insights-btn {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--pf);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
  opacity: 0.7;
  transition: opacity 0.15s;
}

.rp-card__insights-btn:hover,
.rp-card__insights-btn--open { opacity: 1; }

.rp-card__insights-spark { color: var(--pf); }

.rp-card__insights-body {
  margin-top: 10px;
  padding: 13px 15px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--pf) 5%, #fff), var(--surface));
  border: 1px solid color-mix(in srgb, var(--pf) 14%, var(--border));
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rp-card__insight-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rp-card__insight-label {
  font-size: 10px;
  font-weight: 800;
  color: var(--pf);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.rp-card__insight-text {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 20px;
}

/* ── List Sections ──────────────────────────────────────────────────── */

.rp-list-section {
  background-color: var(--card-bg);
  border-radius: var(--radius);
  margin-bottom: 20px;
  border: 1px solid var(--border);
  overflow: hidden;
}

.rp-list-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.rp-list-section__header:hover {
  background-color: var(--surface);
}

.rp-list-section__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rp-list-section__name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.rp-list-section__badge {
  background-color: var(--accent);
  border-radius: 10px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
}

.rp-list-section__body {
  padding: 0 20px 20px;
}

/* Hour Groups */
.rp-hour-group {
  padding-top: 18px;
}

.rp-hour-group:not(:first-child) {
  border-top: 1px solid var(--border);
}

.rp-hour-group__label {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: var(--accent);
  background-color: #e0f7fa;
  padding: 3px 10px;
  border-radius: 8px;
  margin-bottom: 14px;
}

/* ── Followers Pill ────────────────────────────────────────────────── */

.rp-card__followers {
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
  padding: 2px 8px;
  background-color: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  font-weight: 600;
}

/* "x3 מופעים" — this card represents N collapsed duplicates (matches the PDF badge). */
.rp-card__dupes {
  font-size: 10px;
  font-weight: 700;
  color: #5f6368;
  background: #eef0f3;
  border-radius: 8px;
  padding: 2px 6px;
  white-space: nowrap;
}

/* ── Search ────────────────────────────────────────────────────────── */

.rp-search {
  position: relative;
  margin-bottom: 20px;
}

.rp-search__icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.rp-search__input {
  width: 100%;
  padding: 12px 42px 12px 38px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background-color: var(--card-bg);
  font-size: 14px;
  font-family: inherit;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}

.rp-search__input:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.08);
}

.rp-search__input::placeholder {
  color: var(--text-muted);
}

.rp-search__clear {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: background 0.12s;
}

.rp-search__clear:hover {
  background: var(--border-strong);
}

.rp-search__no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 16px;
  color: var(--text-muted);
  font-size: 15px;
  font-weight: 600;
}

/* ── Totals + Trends header ─────────────────────────────────────────── */

.rp-totals {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 10px;
  margin: 14px auto 0;
  flex-wrap: wrap;
  padding: 0 16px;
}

.rp-totals__count {
  font-size: 22px;
  font-weight: 800;
  color: #17365d;
  font-variant-numeric: tabular-nums;
}

.rp-totals__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.rp-totals__trends {
  display: flex;
  gap: 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
}

.rp-totals__pct--up { color: var(--positive); }
.rp-totals__pct--down { color: var(--negative); }

/* ── Sentiment distribution (daily + per-list) ──────────────────────── */

.rp-dist { margin: 0; }

.rp-dist__title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-align: center;
}

.rp-section__sub {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
}

.rp-list-section__dist {
  padding-top: 16px;
}

/* ── Keyword highlight ──────────────────────────────────────────────── */

.rp-mark {
  background: #fff176;
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

/* ── Tweet dependency embeds (reply / quote) ────────────────────────── */

.rp-card__deps {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rp-dep {
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  padding: 8px 10px;
  direction: rtl;
  text-align: right;
}

.rp-dep__rel {
  font-size: 9px;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.rp-dep__head {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 3px;
}

.rp-dep__avatar,
.rp-dep__avatar-img {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rp-dep__avatar {
  background: #1d9bf0;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rp-dep__avatar-img {
  object-fit: cover;
}

.rp-dep__name {
  font-size: 10px;
  font-weight: 700;
  color: var(--text-primary);
}

.rp-dep__handle {
  font-size: 9.5px;
  color: var(--text-muted);
}

.rp-dep__body {
  font-size: 10px;
  line-height: 1.4;
  color: var(--text-primary);
}

.rp-dep__body[dir="ltr"] { text-align: left; }

.rp-dep__open {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 3px 10px;
  background: #0f1419;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  border-radius: 999px;
  text-decoration: none;
  direction: rtl;
}
`;

export { formatDate };
export default DailyReportView;
